import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PortfolioCard } from '../components/PortfolioCard';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const { t, language, getLocalizedLink } = useLanguage();

  const categories = language === 'fr'
    ? ['all', 'Cuisine', 'Salle de bain', 'Commercial', 'Industriel', 'Résidentiel']
    : ['all', 'Kitchen', 'Bathroom', 'Commercial', 'Industrial', 'Residential'];

  const categoryMap = {
    'Cuisine': 'Kitchen',
    'Salle de bain': 'Bathroom',
    'Industriel': 'Industrial',
    'Résidentiel': 'Residential'
  };

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${API}/portfolio`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(p => {
        const mappedCategory = categoryMap[activeCategory] || activeCategory;
        return p.category === mappedCategory || p.category === activeCategory;
      });

  return (
    <div className="grain-texture pt-20" data-testid="portfolio-page">
      {/* Hero */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4 animate-fade-in-up">
              {t('portfolio.tagline')}
            </p>
            <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
              {t('portfolio.title1')} <span className="text-primary">{t('portfolio.title2')}</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed animate-fade-in-up animation-delay-200">
              {t('portfolio.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Filter Tabs */}
          <Tabs defaultValue="all" className="mb-12" onValueChange={setActiveCategory}>
            <TabsList className="bg-muted/50 p-1 rounded-full inline-flex flex-wrap" data-testid="portfolio-tabs">
              {categories.map((category) => (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid={`tab-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category === 'all' ? t('portfolio.allProjects') : category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-muted animate-pulse aspect-[4/3] rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div key={project.id} className="stagger-item">
                  <PortfolioCard project={project} />
                </div>
              ))}
            </div>
          )}

          {filteredProjects.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t('portfolio.noProjects')}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-6">
            {t('portfolio.readyToStart')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
            {t('portfolio.letUsHelp')}
          </p>
          <Button asChild size="lg" className="rounded-full px-8 btn-animate" data-testid="portfolio-cta-btn">
            <Link to={getLocalizedLink('/contact')}>
              {t('portfolio.getFreeQuote')} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
